/** Extracts all per-frame concealment state from GameScene's update loop. */
import { Player } from '../entities/Player';
import { HideSkillEffect } from '../skills/player/HideSkillEffect';
import { StealthSkillEffect } from '../skills/player/StealthSkillEffect';
import { ConfigLoader } from '../systems/ConfigLoader';
import { UpgradeEffectResolver } from '../systems/UpgradeEffectResolver';
import type { PlayerHideState } from '../utils/Types';

/** Everything GameScene's update() needs to know about concealment this frame. */
export interface FrameConcealmentState {
  /** Hide state for enemy perception (undefined = player is visible). */
  playerHideState: PlayerHideState | undefined;
  /** True when player is hidden or stealthed (used by Trace/Marked renderers). */
  isConcealed: boolean;
  /** True when player should render at reduced alpha. */
  isInvisible: boolean;
  /** Stealth step budget remaining (for HUD display), or undefined when stealth is inactive. */
  stealthStepsRemaining: number | undefined;
  /** Feed movement distance to stealth step counter. No-op when stealth is inactive. */
  feedStealthMovement: (distancePx: number) => void;
}

/**
 * Fetch the hide/stealth effects from the player, compute the PlayerHideState enemies
 * use for perception, and gather the auxiliary flags GameScene's update loop needs —
 * so the scene never touches the concrete effect classes.
 */
export function computeFrameConcealmentState(
  player: Player,
  deathState: 'alive' | 'dying' | 'dead' | 'respawning',
  upgradeResolver: UpgradeEffectResolver,
): FrameConcealmentState {
  const hideEff = player.skillActivation.getEffect<HideSkillEffect>('hide');
  const stealthEff = player.skillActivation.getEffect<StealthSkillEffect>('stealth');

  const hideActive = hideEff?.isActive() ?? false;
  const stealthActive = stealthEff?.isActive() ?? false;

  const silentDash = player.dash.isDashing && player.dash.suppressDashGeneratesNoise;
  let hideState = buildPlayerHideState(deathState, hideEff, stealthEff, upgradeResolver);
  if (silentDash) {
    if (hideState) {
      hideState.suppressSoundDetection = true;
    } else {
      hideState = {
        isHidden: false,
        successChance: 0,
        proximityDistancePx: 0,
        proximityCheck: () => false,
        suppressSoundDetection: true,
      };
    }
  }

  return {
    playerHideState: hideState,
    isConcealed: hideActive || stealthActive,
    isInvisible: hideActive || stealthActive,
    stealthStepsRemaining: stealthActive ? stealthEff!.stepsRemaining : undefined,
    feedStealthMovement: stealthActive
      ? (d) => stealthEff!.addMovementDistance(d)
      : () => {},
  };
}

/** Build the PlayerHideState that enemies use for perception checks this frame. */
function buildPlayerHideState(
  deathState: 'alive' | 'dying' | 'dead' | 'respawning',
  hideEff: HideSkillEffect | undefined,
  stealthEff: StealthSkillEffect | undefined,
  upgradeResolver: UpgradeEffectResolver,
): PlayerHideState | undefined {
  if (deathState === 'dying') {
    return {
      isHidden: true,
      successChance: 1,
      proximityDistancePx: 0,
      proximityCheck: () => false,
    };
  }

  if (deathState !== 'alive') return undefined;

  if (stealthEff?.isActive()) {
    const baseStealth = ConfigLoader.stealthTuning.proximityCheckDistancePx;
    const stealthProxMult = upgradeResolver.getProximityMultiplier('hide');
    return {
      isHidden: true,
      successChance: stealthEff.currentSuccessChance,
      proximityDistancePx: baseStealth * stealthProxMult,
      proximityCheck: () => stealthEff.proximityCheck(),
    };
  }

  if (hideEff?.isActive()) {
    const baseProximity = ConfigLoader.hideTuning.proximityCheckDistancePx;
    const proximityMult = upgradeResolver.getProximityMultiplier('hide');
    return {
      isHidden: true,
      successChance: hideEff.currentSuccessChance,
      proximityDistancePx: baseProximity * proximityMult,
      proximityCheck: () => hideEff.proximityCheck(),
    };
  }

  return undefined;
}
